"use client";
import React, { JSX } from "react";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Image from "next/image";
import {
  Menu as MenuIcon,
  MapPin,
  UserIcon,
  CircleHelpIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Menu,
  MenuItem as NavMenuItem,
  HoveredLink,
  ProductItem,
} from "@/components/ui/navbar-menu";

interface MenuItemType {
  title: string;
  url: string;
  description?: string;
  icon?: JSX.Element;
  items?: MenuItemType[];
}

interface Navbar1Props {
  logo?: {
    url: string;
    src: string;
    alt: string;
    title: string;
  };
  menu?: MenuItemType[];
  mobileExtraLinks?: {
    name: string;
    url: string;
  }[];
  auth?: {
    login: {
      text: string;
      url: string;
    };
    signup: {
      text: string;
      url: string;
    };
  };
}

const Navbar1 = ({
  logo = {
    url: "/",
    src: "#",
    alt: "logo",
    title: "Enrumsventilation.se",
  },
  menu = [
    { title: "Hem", url: "/" },
    {
      title: "En rumsventilation",
      url: "/#rumsventilation",
      items: [
        {
          title: "Mini-FTX",
          url: "/mini-ftx",
          description:
            "Modern rumsventilation med värmeåtervinning för enskilda rum.",
        },
        {
          title: "PAX-fläkt",
          url: "/pax-flakt",
          description:
            "Tyst och effektiv ventilation för toaletter och badrum.",
        },
        {
          title: "Tilluftsventiler",
          url: "/tilluftsventiler",
          description: "Tillför frisk luft och förbättrar inomhusklimatet.",
        },
        {
          title: "Kontakta installatör",
          url: "/ventilation-installation",
          description: "Få hjälp med installation av ventilation.",
        },
      ],
    },
    {
      title: "Kunskapsbanken",
      url: "/kunskapsbank",
    },
    {
      title: "Behöver du hjälp?",
      url: "/ventilation-installation",
    },
  ],
  auth = {
    login: { text: "08-604 02 40", url: "tel:08-604 02 40" },
    signup: { text: "Kontakt Formulär", url: "/#contact" },
  },
}: Navbar1Props) => {
  const [openSub, setOpenSub] = useState<string | null>(null);
  const [activeNav, setActiveNav] = useState<string | null>(null);

  const handleSubToggle = (title: string) => {
    setOpenSub((prev) => (prev === title ? null : title));
  };
  const renderMenuItem = (item: MenuItemType) => {
    if (item.items) {
      return (
        <NavigationMenuItem key={item.title} className="text-foreground">
          <NavigationMenuTrigger className="px-5 py-5 text-md">
            {item.title}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <ul className="p-2 font-semibold w-75">
              {item.items.map((subItem, index) => (
                <li key={subItem.title + index} className="ml-2">
                  {subItem.items ? (
                    // Hantera items med submenyer
                    <div className="group">
                      <div
                        className="flex items-center justify-between p-2 cursor-pointer hover:bg-muted"
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubToggle(subItem.title);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {subItem.icon}
                          <a href={subItem.url} className="hover:text-primary">
                            {subItem.title}
                          </a>
                        </div>
                        {subItem.items && <ChevronDown className="size-4" />}
                      </div>
                      {subItem.items && openSub === subItem.title && (
                        <ul className="ml-5">
                          {subItem.items.map((child) => (
                            <li key={child.title}>
                              <a
                                href={child.url}
                                className="block p-2 text-sm hover:text-primary"
                              >
                                {child.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    // Hantera vanliga länkar utan submenyer
                    <a
                      href={subItem.url}
                      className="flex items-center gap-2 p-2 hover:bg-muted"
                    >
                      {subItem.icon}
                      <span>{subItem.title}</span>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        </NavigationMenuItem>
      );
    }

    return (
      <NavigationMenuItem key={item.title}>
        <NavigationMenuLink asChild>
          <a
            className="inline-flex items-center justify-center h-10 px-4 py-2 transition-colors rounded-md text-md group w-max bg-background text-foreground hover:bg-muted hover:text-primary"
            href={item.url}
          >
            {item.title}
          </a>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  };

  const renderMobileMenuItem = (item: MenuItemType) => {
    if (item.items) {
      return (
        <AccordionItem key={item.title} value={item.title}>
          <AccordionTrigger className="py-0 font-semibold">
            {item.title}
          </AccordionTrigger>
          <AccordionContent className="mt-2">
            {item.items.map((subItem) => (
              <div key={subItem.title}>
                {subItem.items ? (
                  <Accordion className="ml-4" type="single" collapsible>
                    <AccordionItem value={subItem.title} className="border-b-0">
                      <AccordionTrigger className="py-2 hover:no-underline">
                        <div className="flex items-center gap-2">
                          {subItem.icon}
                          <span>{subItem.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="ml-4">
                        {subItem.items.map((childItem) => (
                          <a
                            key={childItem.title}
                            href={childItem.url}
                            className="flex items-center gap-2 p-2 text-sm hover:text-primary"
                          >
                            {childItem.title}
                          </a>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <a
                    href={subItem.url}
                    className="flex gap-4 p-3 leading-none transition-colors rounded-md outline-none select-none hover:bg-muted hover:text-accent-foreground"
                  >
                    {subItem.icon}
                    <div>
                      <div className="text-sm font-semibold">
                        {subItem.title}
                      </div>
                      {subItem.description && (
                        <p className="text-sm leading-snug text-muted-foreground">
                          {subItem.description}
                        </p>
                      )}
                    </div>
                  </a>
                )}
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>
      );
    }

    return (
      <a key={item.title} href={item.url} className="block py-2 font-semibold">
        {item.title}
      </a>
    );
  };

  return (
    <section className="sticky top-0 z-50 py-2 bg-background">
      <div className="w-full px-4 mx-auto">
        <nav className="items-center justify-between hidden lg:flex">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href={logo.url} className="flex items-center">
              <Image
                src="/Bilder/Logos/new_logo.webp"
                alt="Enrumsventilation.se logotyp"
                width={180}
                height={54}
                priority
                className="h-auto w-[220px]"
              />
            </a>
          </div>

          {/* Center Menu */}
          <div className="flex justify-center flex-grow">
            <Menu setActive={setActiveNav}>
              <NavMenuItem
                setActive={setActiveNav}
                active={activeNav}
                item="En rumsventilation"
              >
                <div className="grid grid-cols-2 gap-10 p-4 text-sm">
                  <ProductItem
                    title="Mini-FTX"
                    href="/mini-ftx"
                    src="/Bilder/Mini-FTX/mini-ftx.webp"
                    description="Modern rumsventilation med värmeåtervinning för enskilda rum."
                  />
                  <ProductItem
                    title="PAX-fläkt"
                    href="/pax-flakt"
                    src="/Bilder/Houses/flakt-toa.webp"
                    description="Tyst och effektiv ventilation för toaletter och badrum."
                  />
                  <ProductItem
                    title="Tilluftsventiler"
                    href="/tilluftsventiler"
                    src="/Bilder/Mini-FTX/SH - Vilea billeder P2_-8.webp"
                    description="Tillför frisk luft och förbättrar inomhusklimatet."
                  />
                  <a
                    href="/ventilation-installation"
                    className="flex flex-col justify-center p-4 transition-colors rounded-lg hover:bg-accent group"
                  >
                    <h4 className="mb-1 text-base font-bold leading-none text-foreground group-hover:text-primary">
                      Kontakta installatör
                    </h4>
                    <p className="text-sm leading-snug text-muted-foreground line-clamp-2">
                      Få hjälp med installation av ventilation.
                    </p>
                  </a>
                </div>
              </NavMenuItem>
              <a
                href="/kunskapsbank"
                className="inline-flex items-center justify-center h-10 px-4 py-2 transition-colors text-md text-foreground hover:text-primary"
              >
                Kunskapsbanken
              </a>
              <a
                href="/ventilation-installation"
                className="inline-flex items-center justify-center h-10 px-4 py-2 transition-colors text-md text-foreground hover:text-primary"
              >
                Behöver du hjälp?
              </a>
            </Menu>
          </div>

          {/* Buttons */}
          <div className="flex flex-shrink-0 gap-4">
            <Button
              asChild
              size="lg"
              variant="default"
              className="px-6 py-3 text-sm font-semibold rounded-full shadow-lg bg-primary"
            >
              <a href="tel:08-604 02 40">08-604 02 40</a>
            </Button>
          </div>
        </nav>

        <div className="block lg:hidden">
          <div className="flex items-center justify-between">
            <a href={logo.url} className="flex items-center">
              <Image
                src="/Bilder/Logos/new_logo.webp"
                alt="Enrumsventilation.se logotyp"
                width={160}
                height={48}
                priority
                className="h-auto w-[160px]"
              />
            </a>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className=" rounded-4xl">
                  <MenuIcon className="size-4" aria-label="Öppna meny" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>
                    <a href={logo.url} className="flex items-center">
                      <Image
                        src="/Bilder/Logos/new_logo.webp"
                        alt="Enrumsventilation.se logotyp"
                        width={160}
                        height={48}
                        className="h-auto w-[160px]"
                      />
                    </a>
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-6 my-6">
                  <Accordion
                    type="single"
                    collapsible
                    className="flex flex-col w-full gap-4"
                  >
                    {menu.map((item) => renderMobileMenuItem(item))}
                  </Accordion>
                  <div className="flex flex-row flex-wrap items-center justify-center gap-3">
                    <Button
                      asChild
                      variant="default"
                      className="px-10 font-semibold shadow-lg rounded-4xl"
                    >
                      <a href={auth.login.url}>{auth.login.text}</a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Navbar1 };
